/**
 * Represents a file picked by the user before upload.
 * Used to pass attachment data from the input component to the upload flow.
 */
export type AttachmentToPick = {
  uri: string // file:// on native, blob: or data: on web
  name: string
  mime: string
  size: number
  width: number
  height: number
}

/**
 * Content type detection from MIME type.
 */
export function msgtypeFromMime(
  mime: string,
): 'm.image' | 'm.video' | 'm.audio' | 'm.file' {
  if (mime.startsWith('image/')) return 'm.image'
  if (mime.startsWith('video/')) return 'm.video'
  if (mime.startsWith('audio/')) return 'm.audio'
  return 'm.file'
}

// ---------------------------------------------------------------------------
// Matrix connection state machine
// Mirrors the ATProto ConvoStatus pattern (src/state/messages/convo/types.ts)
// ---------------------------------------------------------------------------

export enum MatrixConnectionStatus {
  /** No client created yet */
  Uninitialized = 'uninitialized',
  /** Client created, startClient() in progress */
  Connecting = 'connecting',
  /** Initial sync underway (PREPARED not yet received) */
  Syncing = 'syncing',
  /** Sync PREPARED/SYNCING — fully operational */
  Ready = 'ready',
  /** App backgrounded — client stopped, can resume quickly */
  Backgrounded = 'backgrounded',
  /** Long background (>5min) — needs full re-sync on resume */
  Suspended = 'suspended',
  /** Sync or connection failed — has error + retry() */
  Error = 'error',
}

export enum MatrixErrorCode {
  InitFailed = 'initFailed',
  SyncFailed = 'syncFailed',
  NetworkError = 'networkError',
}

export type MatrixConnectionError = {
  code: MatrixErrorCode
  exception?: Error
  retry: () => void
}

export enum MatrixDispatchEvent {
  Init = 'init',
  StartSync = 'startSync',
  Ready = 'ready',
  Background = 'background',
  Resume = 'resume',
  Suspend = 'suspend',
  Error = 'error',
}

export type MatrixDispatch =
  | {event: MatrixDispatchEvent.Init}
  | {event: MatrixDispatchEvent.StartSync}
  | {event: MatrixDispatchEvent.Ready}
  | {event: MatrixDispatchEvent.Background}
  | {event: MatrixDispatchEvent.Resume}
  | {event: MatrixDispatchEvent.Suspend}
  | {event: MatrixDispatchEvent.Error; payload: MatrixConnectionError}

const ACTIVE_STATUSES = new Set([
  MatrixConnectionStatus.Ready,
  MatrixConnectionStatus.Backgrounded,
  MatrixConnectionStatus.Suspended,
])

/** Check if the Matrix connection is in a state where operations can proceed */
export function isMatrixReady(status: MatrixConnectionStatus): boolean {
  return ACTIVE_STATUSES.has(status)
}

/** Inactivity threshold before Backgrounded → Suspended (5 minutes) */
export const INACTIVE_TIMEOUT = 5 * 60 * 1000
